import { useEffect, useMemo, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Checkbox,
} from '@sib/ui';
import { firstLetterUppercase } from '@sib/utils';
import { SibTableVisibilitySettings } from '../../../types';

interface SibTableSettingsDialogProps<TData> {
  table: Table<TData>;
  open: boolean;
  onClose: () => void;
  onSave?: (visibility: SibTableVisibilitySettings) => void;
  disabledColumns?: Set<string>;
}

export const SibTableSettingsDialog = <TData,>({
  table,
  open,
  onClose,
  onSave,
  disabledColumns = new Set(),
}: SibTableSettingsDialogProps<TData>) => {
  const { t } = useTranslation();

  const [visibility, setVisibility] = useState<SibTableVisibilitySettings>({});

  const allCols = useMemo(
    () =>
      table
        .getAllLeafColumns()
        .filter(
          col =>
            col.id !== 'select' &&
            col.id !== 'actions' &&
            !disabledColumns.has(col.id)
        ),
    [table, disabledColumns]
  );

  useEffect(() => {
    if (open) {
      const current = allCols.reduce<SibTableVisibilitySettings>((acc, col) => {
        acc[col.id] = col.getIsVisible();

        return acc;
      }, {});

      setVisibility(current);
    }
  }, [open, allCols]);

  const handleCheckboxChange = (colId: string, checked: boolean) => {
    setVisibility(prev => ({
      ...prev,
      [colId]: checked,
    }));
  };

  const handleSave = () => {
    table.setColumnVisibility({ ...visibility });
    onSave?.(visibility);
    onClose();
  };

  const selectedCount = Object.values(visibility).filter(Boolean).length;
  const allSelected = selectedCount === allCols.length;

  const handleSelectAll = (checked: boolean) => {
    const newVisibility = allCols.reduce<SibTableVisibilitySettings>(
      (acc, col) => {
        acc[col.id] = checked;

        return acc;
      },
      {}
    );

    setVisibility(newVisibility);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[727px] gap-5">
        <DialogHeader className="gap-5">
          <DialogTitle className="text-primary text-center text-xl">
            {t('components.dataTable.columnVisibilitySettings.title')}
          </DialogTitle>
          <p className="text-primary text-center text-sm">
            {t('components.dataTable.columnVisibilitySettings.subtitle')}
          </p>
        </DialogHeader>
        <div className="flex flex-col rounded-md border">
          <div className="flex items-center gap-2 border-b p-3">
            <Checkbox
              checked={allSelected}
              onCheckedChange={checked => handleSelectAll(!!checked)}
            />
            <span className="font-medium">{t('selectAll')}</span>
          </div>

          <div className="max-h-[340px] overflow-y-auto">
            {allCols.map(col => {
              const checked = visibility[col.id] ?? col.getIsVisible();
              const label =
                col.columnDef.header &&
                typeof col.columnDef.header !== 'function'
                  ? String(col.columnDef.header)
                  : col.id;

              return (
                <label key={col.id} className="flex items-center gap-2 p-3">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={checked =>
                      handleCheckboxChange(col.id, !!checked)
                    }
                  />
                  <span>{firstLetterUppercase(label)}</span>
                </label>
              );
            })}
          </div>
          <div className="text-muted-foreground border-t p-3 text-sm">
            {t('components.dataTable.columnVisibilitySettings.selectedCount', {
              selected: selectedCount,
              total: allCols.length,
            })}
          </div>
        </div>
        <div className="flex justify-center gap-4 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('components.dataTable.columnVisibilitySettings.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={selectedCount === 0}
          >
            {t('components.dataTable.columnVisibilitySettings.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
